import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  StepperLayout,
  useStepperState,
} from '../../../../components/layouts/stepper/StepperLayout';
import {useRqListPrepopulatedStations} from '../../../../react_query/stations/OnboardStationList';
import AssignTaskButton from '../../../../components/my-task/AssignTaskButton';
import DialogApiRequestStatus from '../../../../components/dialogs/DialogApiRequestStatus';
import StationLabeling from './OnboardingStationsCmpt';
import {IStation} from '../../../../dto/Station.dto';
import {apiPost} from '../../../../axiosConfig';
import {Stack} from '@mui/material';

type Props = {
  taskId: number;
  handleCancel: () => void;
};

export type IPrimaryStations = {
  full_station: number;
  odo_station: number;
  mirror_station: number;
};

export const defaultPrimaryStations = {
  full_station: 0,
  odo_station: 0,
  mirror_station: 0,
};

const OnboardingStationsRequest = ({taskId, handleCancel}: Props) => {
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [stations, setStations] = useState<IStation[]>([]);
  const [isMirrorStation, setIsMirrorStation] = useState<boolean>(false);
  const [isODOStation, setIsODOStation] = useState<boolean>(false);
  const {activeStepIndex, onNextStep, onPreviousStep, onStepClick} =
    useStepperState(0);
  const [primaryStations, setPrimaryStations] = useState<IPrimaryStations>(
    defaultPrimaryStations,
  );

  const {data, isFetching, isError} = useRqListPrepopulatedStations(taskId);

  const [apiState, setApiState] = useState({status: '', error: null});

  const steps = useMemo(() => {
    const fullStationDetailsFilled = () => {
      return stations.some(
        val =>
          val.type === 'full_station' &&
          (val.name.length === 0 || primaryStations.full_station === 0),
      );
    };
    const odoStationDetailsFilled = () => {
      return stations.some(
        val =>
          val.type === 'odo_station' &&
          (val.name.length === 0 || primaryStations.odo_station === 0),
      );
    };
    const mirrorStationDetailsFilled = () => {
      return stations.some(
        val =>
          val.type === 'mirror_station' &&
          (val.name.length === 0 || val.parent_id === null),
      );
    };
    const stepList: {
      label: string;
      disabled: boolean;
      isReviewDisabled: boolean;
    }[] = [];

    if (stations.some(o => o.type === 'full_station')) {
      stepList.push({
        label: 'Full Station',
        disabled: false,
        isReviewDisabled: false,
      });
    }

    if (isMirrorStation) {
      stepList.push({
        label: 'Mirror Station',
        disabled: fullStationDetailsFilled(),
        isReviewDisabled: mirrorStationDetailsFilled(),
      });
    } else if (isODOStation) {
      stepList.push({
        label: 'ODO Station',
        disabled: fullStationDetailsFilled(),
        isReviewDisabled: odoStationDetailsFilled(),
      });
    }
    return stepList;
  }, [
    isMirrorStation,
    isODOStation,
    primaryStations.full_station,
    primaryStations.odo_station,
    stations,
  ]);

  const handleSubmit = useCallback(() => {
    setApiState({status: 'pending', error: null});
    const data = stations.map(val => {
      if (val.type === 'full_station') {
        return {
          ...val,
          is_primary_station: primaryStations.full_station === val.id,
        };
      } else {
        return {
          ...val,
          is_primary_station: primaryStations.odo_station === val.id,
        };
      }
    });
    const payload = {
      stations: data,
    };
    apiPost(`/v1/task/${taskId}/stations`, payload)
      .then(() => {
        setApiState({status: 'success', error: null});
      })
      .catch(error => {
        setApiState({status: 'error', error});
      });
  }, [
    primaryStations.full_station,
    primaryStations.odo_station,
    stations,
    taskId,
  ]);

  // option to edit the form when api is failed
  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null});
  }, []);

  const handleClose = useCallback(() => {
    setApiState({status: '', error: null});
    handleCancel();
  }, [handleCancel]);

  useEffect(() => {
    if (data && !isFetching && !isError) {
      setStations(data);
      setIsMirrorStation(data.some(val => val.type === 'mirror_station'));
      setIsODOStation(data.some(val => val.type === 'odo_station'));
    }
  }, [data, isError, isFetching]);
  return (
    <StepperLayout
      steps={steps}
      activeStepIndex={activeStepIndex}
      onBack={onPreviousStep}
      onNext={onNextStep}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      onStepClick={onStepClick}
      isSubmitted={apiState.status !== ''}>
      <Stack direction={'row'} spacing={2} mt={2} justifyContent={'flex-end'}>
        <AssignTaskButton isPreview={isPreview} setIsPreview={setIsPreview} task_id={taskId} />
      </Stack>
      {(() => {
        const label = steps?.[activeStepIndex]?.label ?? '';
        switch (label) {
          case 'Full Station':
            return (
              <StationLabeling
                stations={stations}
                type='full_station'
                setStations={setStations}
                primaryStations={primaryStations}
                setPrimaryStations={setPrimaryStations}
              />
            );
          case 'Mirror Station':
            return (
              <StationLabeling
                stations={stations}
                type='mirror_station'
                setStations={setStations}
                primaryStations={primaryStations}
                setPrimaryStations={setPrimaryStations}
              />
            );
          case 'ODO Station':
            return (
              <StationLabeling
                stations={stations}
                type='odo_station'
                setStations={setStations}
                primaryStations={primaryStations}
                setPrimaryStations={setPrimaryStations}
              />
            );
          default:
            return null;
        }
      })()}
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleSubmit}
          onEdit={handleEdit}
          onClose={handleClose}
        />
      )}
    </StepperLayout>
  );
};

export default OnboardingStationsRequest;
